import styled from "@emotion/styled";
import { ErrorBoundary } from "react-error-boundary";
import type { WrapperComponent } from "types";
import { isProd } from "config";

/**
 * Top-most error fallback UI for all uncaught render errors
 *
 * NOTE: this uses hardcoded text because the error boundary is higher in the component tree than
 * the internationlization context, meaning it can catch errors in any of the app providers.
 */
function GlobalErrorFallback({ error }: { error: Error }) {
  return (
    <Container role="alert">
      <h1>Oh no, we broke something!</h1>
      <p>
        Reload the page and give it another shot. We’ll try better next time.
      </p>
      <button type="button" onClick={() => window.location.reload()}>
        Reload
      </button>

      {!isProd && <Pre>{error.stack}</Pre>}
    </Container>
  );
}

const Container = styled.div(({ theme }) => ({
  alignItems: "center",
  background: "#f7cfda",
  color: "#262626",
  display: "flex",
  flexDirection: "column",
  fontSize: "1.2rem",
  justifyContent: "center",
  minHeight: "100vh",
  minWidth: "100vw",
  h1: {
    marginBottom: "2rem",
  },
  button: {
    background: "white",
    border: "2px solid #262626",
    borderRadius: theme.borderRadius.sm,
    cursor: "pointer",
    fontSize: "1.2rem",
    margin: "1rem",
    padding: "0.4rem 1.2rem",
  },
}));

const Pre = styled.pre({
  background: "#fBf2c3",
  fontSize: "1rem",
  padding: "1rem",
});

function GlobalErrorBoundary({ children }: WrapperComponent) {
  return (
    <ErrorBoundary FallbackComponent={GlobalErrorFallback}>
      {children}
    </ErrorBoundary>
  );
}

export { GlobalErrorBoundary };
